import React, { useState, useEffect, useCallback, useMemo } from "react";
import DataTable from "./components/DataTable";
import Select from "./components/Select";
import EditSecuritiesDialog from "./components/AddSecuritiesDialog";
import CreateUniverseDialog from "./components/CreateUniverseDialog";
import EditUniverseDialog from "./components/EditUniverseDialog";
import SecurityDetailsDialog from "./components/SecurityDetailsDialog";
import LookupIdentifiersDialog from "./components/LookupIdentifiersDialog";
import ClientManagement from "./components/ClientManagement";
import Login from "./components/Login";
import Button from "./components/Button";

const API_BASE = "/v1";
const ADMIN_API_BASE = "/v1/admin";

function getScopesFromToken(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (payload.scope || '').split(' ');
  } catch {
    return [];
  }
}

function App() {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [data, setData] = useState([]);
  const [universes, setUniverses] = useState([]);
  const [universeId, setUniverseId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedRows, setSelectedRows] = useState([]);
  const [activeTab, setActiveTab] = useState("universes");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditUniverseOpen, setIsEditUniverseOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsIndex, setDetailsIndex] = useState(0);
  const [isLookupDialogOpen, setIsLookupDialogOpen] = useState(false);

  const scopes = useMemo(() => token ? getScopesFromToken(token) : [], [token]);
  const isAdmin = useMemo(() => scopes.includes("v1.admin.universes"), [scopes]);
  const canManageClients = useMemo(() => scopes.includes("v1.admin.clients"), [scopes]);

  const handleLogin = (newToken) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setActiveTab("universes");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setData([]);
    setUniverses([]);
    setUniverseId("");
    setActiveTab("universes");
  };

  // Helper to get headers
  const getHeaders = useCallback(() => {
    return token ? { "Authorization": `Bearer ${token}` } : {};
  }, [token]);

  const fetchUniverses = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/universes`, { headers: getHeaders() });
      if (response.ok) {
        const list = await response.json();
        setUniverses(list);
        if (list.length > 0 && !universeId) {
          setUniverseId(list[0].universeId);
        }
      } else if (response.status === 401) {
        handleLogout();
      } else {
        console.error("Failed to fetch universes", response.status);
      }
    } catch (error) {
      console.error("Error fetching universes", error);
    }
  }, [universeId, token, getHeaders]);

  const dataFetcher = useCallback(async (currentUniverseId) => {
    if (!currentUniverseId || !token) return;
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/universes/${currentUniverseId}/content`,
        { headers: getHeaders() }
      );
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else if (response.status === 401) {
        handleLogout();
      } else {
        console.error("Failed to fetch content", response.status);
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching content", error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, getHeaders]);

  useEffect(() => {
    if (token) {
      fetchUniverses();
    }
  }, [fetchUniverses, token]);

  useEffect(() => {
    if (token && universeId) {
      dataFetcher(universeId);
      setSelectedRows([]);
    }
  }, [universeId, dataFetcher, token]);

  useEffect(() => {
    if (activeTab === "clients" && !canManageClients) {
      setActiveTab("universes");
    }
  }, [activeTab, canManageClients]);

  const handleOpenEditDialog = () => setIsEditDialogOpen(true);
  const handleCloseEditDialog = () => setIsEditDialogOpen(false);
  const handleOpenCreateDialog = () => setIsCreateDialogOpen(true);
  const handleCloseCreateDialog = () => setIsCreateDialogOpen(false);
  const handleOpenEditUniverse = () => setIsEditUniverseOpen(true);
  const handleCloseEditUniverse = () => setIsEditUniverseOpen(false);
  const handleViewDetails = useCallback((item, index) => {
    setDetailsIndex(index);
    setIsDetailsOpen(true);
  }, []);
  const handleCloseDetails = useCallback(() => setIsDetailsOpen(false), []);

  const handleOpenLookupDialog = () => setIsLookupDialogOpen(true);
  const handleCloseLookupDialog = () => setIsLookupDialogOpen(false);
  const handleContentUpdated = useCallback(() => dataFetcher(universeId), [dataFetcher, universeId]);
  const handleUniverseCreated = useCallback(() => fetchUniverses(), [fetchUniverses]);

  const handleDeleteUniverse = useCallback(async () => {
    if (!universeId) return;
    if (!window.confirm(`Are you sure you want to delete universe "${universeId}" and all its content?`)) {
      return;
    }
    try {
      const response = await fetch(`${ADMIN_API_BASE}/universes/${universeId}`, {
        method: 'DELETE',
        headers: { ...getHeaders() }
      });
      if (response.ok) {
        setUniverseId("");
        setData([]);
        fetchUniverses();
      } else {
        alert("Errore durante l'eliminazione dell'universo");
      }
    } catch (error) {
      console.error("Error deleting universe", error);
      alert("Errore di connessione");
    }
  }, [universeId, getHeaders, fetchUniverses]);

  const handleAddFromLookup = useCallback(async (newSecIds) => {
    if (!newSecIds || newSecIds.length === 0 || !universeId) return;
    console.log("Adding from lookup:", newSecIds);

    const currentIds = data.map(item => item.SecurityID);
    // Combine existing and new, ensuring uniqueness
    const updatedIds = Array.from(new Set([...currentIds, ...newSecIds]));
    console.log("Updated list to send:", updatedIds);

    try {
      const response = await fetch(`${ADMIN_API_BASE}/universes/${universeId}/content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getHeaders()
        },
        body: JSON.stringify(updatedIds)
      });

      if (response.ok) {
        handleContentUpdated();
      } else {
        alert("Errore durante l'aggiunta delle security");
      }
    } catch (error) {
      console.error("Error adding securities", error);
    }
  }, [data, universeId, getHeaders, handleContentUpdated]);

  const handleRemoveSecurities = useCallback(async (idsToRemove) => {
    if (!idsToRemove || idsToRemove.length === 0 || !universeId) return;
    console.log("Removing securities:", idsToRemove);

    if (!window.confirm(`Are you sure you want to remove ${idsToRemove.length} securities?`)) {
      return;
    }

    const currentIds = data.map(item => item.SecurityID);
    const newIds = currentIds.filter(id => !idsToRemove.includes(id));
    console.log("New IDs list after removal:", newIds);

    try {
      const response = await fetch(`${ADMIN_API_BASE}/universes/${universeId}/content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getHeaders()
        },
        body: JSON.stringify(newIds)
      });

      if (response.ok) {
        handleContentUpdated();
      } else {
        console.error("Failed to update securities", response.status);
        alert("Errore durante l'aggiornamento");
      }
    } catch (error) {
      console.error("Error updating securities", error);
      alert("Errore di connessione");
    }
  }, [data, universeId, getHeaders, handleContentUpdated]);

  const universeOptions = universes.map(u => ({ value: u.universeId, label: u.name || u.universeId }));

  if (!token) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto max-w-6xl space-y-6 px-4 ">
        <header className="flex items-center justify-between gap-3 rounded-lg bg-white px-4 py-3 shadow-sm">
          <div className="flex items-center gap-3">
            <img src="/azimut-logo.png" alt="Azimut logo" className="h-10 w-auto" />
            <p className="text-xl font-semibold uppercase tracking-wide text-slate-800 drop-shadow-sm">
              Azimut
            </p>
            {canManageClients && (
              <div className="ml-4 flex gap-1 rounded-lg border border-slate-200 p-0.5">
                <button
                  onClick={() => setActiveTab("universes")}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${activeTab === "universes" ? "bg-blue-500 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}
                >
                  Universes
                </button>
                <button
                  onClick={() => setActiveTab("clients")}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${activeTab === "clients" ? "bg-blue-500 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}
                >
                  Clients
                </button>
              </div>
            )}
          </div>
          <Button variant="outline" onClick={handleLogout} className="text-sm">Logout</Button>
        </header>
        {activeTab === "universes" ? (
          <>
            <Select
              className="w-full md:w-1/2"
              options={universeOptions}
              placeholder="Seleziona universo"
              ariaLabel="Seleziona universo"
              value={universeId}
              onValueChange={(value) => setUniverseId(value)}
              label="Universi"
              name="universeId"
              id="universeId"
            />
            <DataTable
              className=""
              data={data}
              isLoading={isLoading}
              isAdmin={isAdmin}
              onSelectionChange={setSelectedRows}
              onEditSecuritiesClick={handleOpenEditDialog}
              onLookupIdentifiersClick={handleOpenLookupDialog}
              onRemoveSecurities={handleRemoveSecurities}
          onCreateUniverseClick={handleOpenCreateDialog}
          onDeleteUniverseClick={handleDeleteUniverse}
          onEditUniverseClick={handleOpenEditUniverse}
          onViewDetails={handleViewDetails}
            />
          </>
        ) : (
          <ClientManagement onLogout={handleLogout} />
        )}
      </main>

      {activeTab === "universes" && (
        <>
            <EditSecuritiesDialog
          open={isEditDialogOpen}
          onClose={handleCloseEditDialog}
          universeId={universeId}
          onUpdated={handleContentUpdated}
          data={data}
          isAdmin={isAdmin}
        />
        <CreateUniverseDialog
          open={isCreateDialogOpen}
          onClose={handleCloseCreateDialog}
          onCreated={handleUniverseCreated}
        />
        <EditUniverseDialog
          open={isEditUniverseOpen}
          onClose={handleCloseEditUniverse}
          universe={universes.find((u) => u.universeId === universeId) || null}
          onUpdated={handleContentUpdated}
        />
        <SecurityDetailsDialog
          open={isDetailsOpen}
          onClose={handleCloseDetails}
          data={data}
          initialIndex={detailsIndex}
        />
        <LookupIdentifiersDialog
          open={isLookupDialogOpen}
          onClose={handleCloseLookupDialog}
          universeId={universeId}
          onAdd={handleAddFromLookup}
        />
      </>)}
    </div>
  );
}

export default App;
